import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey } from '@fortawesome/pro-light-svg-icons/faKey';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { OUTREACH_RESUME_CAP, OUTREACH_RESUME_DAYS } from 'fym-shared/src/music24/outreach/mailboxRamp';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import type { MailboxRow } from '../../../music24/outreach/outreachTypes';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
import { mailboxMessages as messages } from './mailboxMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Mailboxes card; each part uses its own slice of it */
import styles from './Mailboxes.module.scss';

interface MailboxNoticeProps {
  mailbox: MailboxRow;
  /** The owner may resume; the owner and the person who connected it may type a new password. */
  canResume: boolean;
  canReconnect: boolean;
  onResume: () => void;
  onReconnect: () => void;
}

function pausedCopy(mailbox: MailboxRow) {
  if (mailbox.pauseCause === 'support') {
    return { title: messages.pausedTitle, body: messages.pausedBySupportBody };
  }
  if (mailbox.pauseCause === 'owner') {
    return { title: messages.pausedByYouTitle, body: messages.pausedByYouBody };
  }
  return { title: messages.pausedTitle, body: messages.pausedHealthBody };
}

/**
 * The alert under a paused or a sign-in failed row (design outreach-v2 q3): the reason and one fix
 * (Resume, Type a new password). A legacy `provisioning` row says to remove it: Music24 sells no
 * mailbox since 2026-10-20. Only Music24 support ends its own pause (owner decision 2026-10-12).
 */
const MailboxNoticeComponent: React.FC<MailboxNoticeProps> = ({
  mailbox,
  canResume,
  canReconnect,
  onResume,
  onReconnect,
}) => {
  const intl = useIntl();
  const { health } = mailbox;
  if (health !== 'paused' && health !== 'reconnect' && health !== 'provisioning') {
    return null;
  }
  if (health === 'provisioning') {
    return (
      <div className={clsx(styles.alert, styles.alertAmber)} data-testid="mailbox-notice-provisioning">
        <span className={styles.alertText}>{withBrandWord(intl.formatMessage(messages.legacyRequestBody))}</span>
      </div>
    );
  }
  const isPaused = health === 'paused';
  const copy = isPaused ? pausedCopy(mailbox) : { title: messages.reconnectTitle, body: messages.reconnectV3Body };
  const showResume = isPaused && canResume && mailbox.pauseCause !== 'support';
  return (
    <div
      className={clsx(styles.alert, isPaused ? styles.alertBad : styles.alertAmber)}
      data-testid={`mailbox-notice-${health}`}
    >
      <FontAwesomeIcon icon={isPaused ? faTriangleExclamation : faKey} className={styles.alertIcon} />
      <span className={styles.alertText}>
        <b>{intl.formatMessage(copy.title)}</b>{' '}
        {withBrandWord(intl.formatMessage(copy.body, { cap: OUTREACH_RESUME_CAP, days: OUTREACH_RESUME_DAYS }))}
        {!isPaused && mailbox.lastError !== null && (
          <span className="d-block text-body-secondary mt-1">
            {intl.formatMessage(messages.serverAnswer, { error: mailbox.lastError })}
          </span>
        )}
      </span>
      {showResume && (
        <SMLButton size="sm" outline omitCap onClick={onResume}>
          {intl.formatMessage(messages.resume)}
        </SMLButton>
      )}
      {!isPaused && canReconnect && (
        <SMLButton size="sm" outline omitCap onClick={onReconnect}>
          {intl.formatMessage(messages.typeNewPassword)}
        </SMLButton>
      )}
    </div>
  );
};

export const MailboxNotice = TypedMemo(MailboxNoticeComponent);
